import React, { useEffect, useState, useCallback } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import Block from "../../../../../components/Tailwind/Block";
import Header from "../../../../../components/Tailwind/Block/Header";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../components/Form";
import overrideClasses from "../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import { ActionTypes, isString } from "../../../../../utils/types";
import history from "../../../../../history";
import { EnumPaths } from "../../../../../utils/navigation";
import { EnumPermissionEntity } from "../../../../../lib/ground-aws-graphql-core/api/graphql/types";
import FormQuill from "../../../../../components/Form/FormQuill";
import {
  Checkpoint,
  CheckpointType,
  SearchableCheckpointTypesFilterInput,
} from "../../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import CheckpointTypeComponent from "../../../../../components/CheckpointType";

interface Props {
  match: Match<{ cid: string; id?: string }>;
  intl: IntlShape;
  edition: boolean;
}

const RoundFormForm: React.FC<Props> = ({ intl, match, edition }) => {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [togglePanel, setTogglePanel] = useState(false);
  const [checkpoints, setCheckpoints] = useState<
    {
      checkpointType: CheckpointType;
      checkpoint: Checkpoint;
      action: ActionTypes;
    }[]
  >([]);

  const roundForm = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.roundForm
  );
  const checkpointTypes = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.checkpointTypes
  );

  const getRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.getRoundForm
  );
  const createRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.createRoundForm
  );
  const updateRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.updateRoundForm
  );
  const setRoundForm = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setRoundForm
  );
  const listCheckpointTypes = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listCheckpointTypes
  );

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "title",
      label: "page.inspection.round.form.label",
      placeholder: "page.inspection.round.form.label.placeholder",
      initialValue: roundForm?.title,
      required: true,
      translatable: true,
    },
    {
      name: "description",
      label: "page.inspection.round.form.description",
      placeholder: "page.inspection.round.form.description.placeholder",
      initialValue: roundForm?.description,
      required: false,
      translatable: true,
      component: formikProps => <FormQuill {...formikProps} />,
    },
    {
      name: "location",
      label: "page.inspection.round.form.location",
      placeholder: "page.inspection.round.form.location.placeholder",
      initialValue: roundForm?.location,
      required: false,
      translatable: true,
    },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);

    const filter: SearchableCheckpointTypesFilterInput = {
      building_id: { eq: match.params.cid },
    };
    const promises = [listCheckpointTypes({ offset: 0, limit: 100, filter })];

    if (match.params.id) {
      promises.push(getRoundForm({ id: match.params.id }));
    }

    try {
      await Promise.all(promises);
    } finally {
      setLoading(false);
    }
  }, [listCheckpointTypes, getRoundForm, match.params.cid, match.params.id]);

  useEffect(() => {
    fetchData();

    return () => setRoundForm(null);
  }, [fetchData, setRoundForm]);

  useEffect(() => {
    if (roundForm) {
      setEnabled(roundForm.enabled);
      const elements = roundForm.checkpoints.map(cp => ({
        checkpointType: cp.type,
        checkpoint: cp,
        action: ActionTypes.TO_KEEP,
      }));
      setCheckpoints(elements);
    }
  }, [roundForm]);

  const showSavePanel = () => setTogglePanel(true);

  const onAddCheckpoint = (type: CheckpointType, checkpoint: Checkpoint) => {
    setCheckpoints(prev => {
      const exists = prev.find(
        el =>
          el.checkpoint.id === checkpoint.id && el.checkpointType.id === type.id
      );
      if (exists && exists.action === ActionTypes.TO_DELETE) {
        return prev.map(el =>
          el.checkpoint.id === checkpoint.id && el.checkpointType.id === type.id
            ? { ...el, action: ActionTypes.TO_KEEP }
            : el
        );
      }

      return [
        ...prev,
        { checkpointType: type, checkpoint, action: ActionTypes.TO_ADD },
      ];
    });
    showSavePanel();
  };

  const onRemoveCheckpoint = (type: CheckpointType, checkpoint: Checkpoint) => {
    setCheckpoints(prev => {
      const exists = prev.find(
        el =>
          el.checkpoint.id === checkpoint.id && el.checkpointType.id === type.id
      );
      if (!exists) return prev;

      if (exists.action === ActionTypes.TO_KEEP && prev.length === 1) {
        toast(
          intl.formatMessage({
            id: "page.inspection.selected.checkpoints.cannot.be.empty",
          }),
          { type: "error" }
        );

        return prev;
      }

      if (exists.action === ActionTypes.TO_KEEP) {
        return prev.map(el =>
          el.checkpoint.id === checkpoint.id && el.checkpointType.id === type.id
            ? { ...el, action: ActionTypes.TO_DELETE }
            : el
        );
      }

      return prev.filter(
        el =>
          el.checkpoint.id !== checkpoint.id || el.checkpointType.id !== type.id
      );
    });
    showSavePanel();
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);

    const checkpointIds = checkpoints
      .filter(el => el.action !== ActionTypes.TO_DELETE)
      .map(el => el.checkpoint.id);

    const payload = {
      ...values,
      title: isString(values.title) ? JSON.parse(values.title) : values.title,
      description: isString(values.description)
        ? JSON.parse(values.description)
        : values.description,
      location: isString(values.location)
        ? JSON.parse(values.location)
        : values.location,
      checkpoints: checkpointIds,
      enabled,
    };

    try {
      if (edition && roundForm) {
        await updateRoundForm({ ...payload, id: roundForm.id });
        toast(
          intl.formatMessage({
            id: "page.inspection.round.form.update.success",
          }),
          { type: "success" }
        );
      } else {
        await createRoundForm({ ...payload, building_id: match.params.cid });
        toast(
          intl.formatMessage({
            id: "page.inspection.round.form.create.success",
          }),
          { type: "success" }
        );
      }
      history.push(
        `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUND_FORMS}`
      );
    } catch {
      toast(
        intl.formatMessage({
          id: edition
            ? "page.inspection.round.form.update.error"
            : "page.inspection.round.form.create.error",
        }),
        { type: "error" }
      );
      history.push(
        `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.ROUND_FORMS}`
      );
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <div className={overrideClasses("px-8 pb-32")}>
      <Block>
        <Header
          item={roundForm}
          checked={enabled}
          title={
            edition
              ? "page.list.inspection.round.forms.update.round.form"
              : "page.list.inspection.round.forms.create.round.form"
          }
          entity={EnumPermissionEntity.ROUND_FORM}
          onChange={e => {
            setEnabled(e);

            // show save panel
            showSavePanel();
          }}
          className="border-b border-gray-200"
        />

        <DefaultForm
          fields={formFields}
          onSubmit={handleSubmit}
          showPanel={togglePanel}
        />
      </Block>

      {checkpointTypes?.items?.map((checkpointType: CheckpointType) => (
        <CheckpointTypeComponent
          key={checkpointType.id}
          item={checkpointType}
          intl={intl}
          onRemoveCheckpoint={onRemoveCheckpoint}
          onAddCheckpoint={onAddCheckpoint}
          items={checkpoints}
        />
      ))}
    </div>
  );
};

export default RoundFormForm;
